import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ─── DTOs ─────────────────────────────────────────────────────────────────────

export interface ChamberPlacementDto {
  germinationChamberId: string;
  traysPlaced: number;
}

export interface ChamberPlacementResponseDto {
  id: string;
  sowingId: string;
  germinationChamberId: string;
  chamberName?: string;
  chamberCode?: string;
  traysPlaced: number;
  placedAt: string;
  placedByUserId?: string;
  placedByUserName?: string;
}

export interface AssignChamberPlacementsRequest {
  sowingId: string;
  placements: ChamberPlacementDto[];
}

export interface SowingDto {
  id: string;
  sowingInstructionId: string;
  instructionNumber?: string;
  sowingDate: string;
  traysSown: number;
  sownByUserId?: string;
  sownByUserName?: string;
  requisitionId?: string;
  requisitionNumber?: string;
  status: string;
  traysGerminated: number;
  traysDiscarded: number;
  traysRemaining: number;
  chamberPlacements: ChamberPlacementResponseDto[];
  unassignedTrays: number;
  createdAt: string;
}

export interface CreateSowingRequest {
  sowingInstructionId: string;
  sowingDate: string;
  traysSown: number;
  chamberPlacements?: ChamberPlacementDto[];
  requisitionId?: string;
}

export interface GerminationDto {
  id: string;
  sowingId: string;
  germinationDate: string;
  traysGerminated: number;
  recordedByUserId?: string;
  recordedByUserName?: string;
  bedAssignments: GerminationBedAssignmentDto[];
  createdAt: string;
}

/** Per-bed tray allocation. Sum of trayCount across the list must equal the wave/entry total. */
export interface BedTrayAllocation {
  bedId: string;
  trayCount: number;
}

export interface RecordGerminationWaveRequest {
  sowingId: string;
  germinationDate: string;
  traysGerminated: number;
  bedAllocations: BedTrayAllocation[];
  greenhouseId?: string;
  inlineDiscardTrays?: number;
  inlineDiscardReason?: string;
}

export interface GerminationBedAssignmentDto {
  id: string;
  germinationId: string;
  greenhouseBedId: string;
  trayCount: number;
  bedNumber?: string;
  bayName?: string;
  greenhouseName?: string;
}

export interface AmendGerminationRequest {
  germinationId: string;
  newTraysGerminated: number;
  newBedAllocations?: BedTrayAllocation[];
  reason: string;
  linkedDiscardRequestId?: string;
}

// ─── Rejection / Amendment ────────────────────────────────────────────────────

export interface SowingInstructionRejectionDto {
  id: string;
  sowingInstructionId: string;
  rejectedByUserId: string;
  rejectedByUserName?: string;
  rejectedAt: string;
  reason: string;
  resolved: boolean;
  resolvedAt?: string;
}

export interface RejectInstructionRequest {
  sowingInstructionId: string;
  reason: string;
}

export interface SowingInstructionAmendmentDto {
  id: string;
  sowingInstructionId: string;
  instructionNumber?: string;
  amendedByUserId: string;
  amendedByUserName?: string;
  amendedAt: string;
  priorQuantityToSow: number;
  newQuantityToSow: number;
  priorScheduledStartDate: string;
  newScheduledStartDate: string;
  priorScheduledEndDate: string;
  newScheduledEndDate: string;
  reason: string;
  status: string;
  decisionByUserId?: string;
  decisionByUserName?: string;
  decisionAt?: string;
  decisionComment?: string;
}

export interface AmendInstructionRequest {
  sowingInstructionId: string;
  newQuantityToSow: number;
  newScheduledStartDate: string;
  newScheduledEndDate: string;
  reason: string;
}

export interface DecideAmendmentRequest {
  amendmentId: string;
  approve: boolean;
  comment?: string;
}

// ─── Cascading Availability ───────────────────────────────────────────────────

export interface AvailableGreenhouseDto {
  id: string;
  greenhouseName: string;
  availableBedCount: number;
}

export interface AvailableBayDto {
  id: string;
  bayName: string;
  availableBedCount: number;
}

export interface AvailableBedDto {
  id: string;
  bedNumber: string;
  bayName?: string;
  plantCapacity?: number;
  currentOccupancy: number;
}

// ─── Requisition Gating ───────────────────────────────────────────────────────

export interface EligibleRequisitionDto {
  id: string;
  requisitionNumber: string;
  status: string;
  requisitionDate: string;
  purpose: string;
}

// ─── Calendar / Planning ──────────────────────────────────────────────────────

export interface CalendarSowingEventDto {
  id: string;
  sowingInstructionId: string;
  instructionNumber?: string;
  variety?: string;
  cropTypeName?: string;
  sowingDate: string;
  traysSown: number;
  germinationChamberName?: string;
  status: string;
}

export interface CalendarInstructionDto {
  id: string;
  instructionNumber: string;
  variety?: string;
  cropTypeName?: string;
  quantityToSow: number;
  totalSown: number;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  scheduledSowingDate: string;
  status: string;
  sowings: CalendarSowingEventDto[];
}

// ─── API Client ───────────────────────────────────────────────────────────────

const BASE = "/sowing-lifecycle";

export const sowingLifecycleApi = {
  // ── Sowing ──────────────────────────────────────────────────────────────────

  getSowingsByInstruction: async (instructionId: string): Promise<SowingDto[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingDto[]>>(
      `${BASE}/instructions/${instructionId}/sowings`
    );
    return data.data ?? [];
  },

  getSowing: async (sowingId: string): Promise<SowingDto> => {
    const { data } = await apiClient.get<ApiResponse<SowingDto>>(
      `${BASE}/sowings/${sowingId}`
    );
    return data.data;
  },

  createSowing: async (payload: CreateSowingRequest): Promise<SowingDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingDto>>(
      `${BASE}/sowings`,
      payload
    );
    return data.data;
  },

  // ── Germination ─────────────────────────────────────────────────────────────

  getChamberPlacements: async (sowingId: string): Promise<ChamberPlacementResponseDto[]> => {
    const { data } = await apiClient.get<ApiResponse<ChamberPlacementResponseDto[]>>(
      `${BASE}/sowings/${sowingId}/chamber-placements`
    );
    return data.data ?? [];
  },

  assignChamberPlacements: async (sowingId: string, placements: ChamberPlacementDto[]): Promise<SowingDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingDto>>(
      `${BASE}/sowings/${sowingId}/chamber-placements`,
      { sowingId, placements }
    );
    return data.data;
  },

  getGerminations: async (sowingId: string): Promise<GerminationDto[]> => {
    const { data } = await apiClient.get<ApiResponse<GerminationDto[]>>(
      `${BASE}/sowings/${sowingId}/germinations`
    );
    return data.data ?? [];
  },

  recordGerminationWave: async (payload: RecordGerminationWaveRequest): Promise<GerminationDto> => {
    const { data } = await apiClient.post<ApiResponse<GerminationDto>>(
      `${BASE}/germinations`,
      payload
    );
    return data.data;
  },

  amendGermination: async (payload: AmendGerminationRequest): Promise<GerminationDto> => {
    const { data } = await apiClient.put<ApiResponse<GerminationDto>>(
      `${BASE}/germinations/amend`,
      payload
    );
    return data.data;
  },

  finalizeGermination: async (instructionId: string): Promise<boolean> => {
    const { data } = await apiClient.post<ApiResponse<boolean>>(
      `${BASE}/instructions/${instructionId}/finalize-germination`
    );
    return data.data;
  },

  closeForSowing: async (instructionId: string): Promise<boolean> => {
    const { data } = await apiClient.post<ApiResponse<boolean>>(
      `${BASE}/instructions/${instructionId}/close-for-sowing`
    );
    return data.data;
  },

  // ── Rejection / Amendment ───────────────────────────────────────────────────

  rejectInstruction: async (payload: RejectInstructionRequest): Promise<SowingInstructionRejectionDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstructionRejectionDto>>(
      `${BASE}/instructions/reject`,
      payload
    );
    return data.data;
  },

  amendInstruction: async (payload: AmendInstructionRequest): Promise<SowingInstructionAmendmentDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstructionAmendmentDto>>(
      `${BASE}/instructions/amend`,
      payload
    );
    return data.data;
  },

  decideAmendment: async (payload: DecideAmendmentRequest): Promise<SowingInstructionAmendmentDto> => {
    const { data } = await apiClient.post<ApiResponse<SowingInstructionAmendmentDto>>(
      `${BASE}/amendments/decide`,
      payload
    );
    return data.data;
  },

  getPendingAmendments: async (): Promise<SowingInstructionAmendmentDto[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstructionAmendmentDto[]>>(
      `${BASE}/amendments/pending`
    );
    return data.data ?? [];
  },

  getAmendmentHistory: async (): Promise<SowingInstructionAmendmentDto[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingInstructionAmendmentDto[]>>(
      `${BASE}/amendments/history`
    );
    return data.data ?? [];
  },

  // ── Cascading Availability ──────────────────────────────────────────────────

  getAvailableGreenhouses: async (): Promise<AvailableGreenhouseDto[]> => {
    const { data } = await apiClient.get<ApiResponse<AvailableGreenhouseDto[]>>(
      `${BASE}/greenhouses/available-for-germination`
    );
    return data.data ?? [];
  },

  getAvailableBays: async (greenhouseId: string): Promise<AvailableBayDto[]> => {
    const { data } = await apiClient.get<ApiResponse<AvailableBayDto[]>>(
      `${BASE}/greenhouses/${greenhouseId}/bays/available`
    );
    return data.data ?? [];
  },

  getAvailableBeds: async (bayId: string): Promise<AvailableBedDto[]> => {
    const { data } = await apiClient.get<ApiResponse<AvailableBedDto[]>>(
      `${BASE}/bays/${bayId}/beds/available`
    );
    return data.data ?? [];
  },

  // ── Calendar / Planning ─────────────────────────────────────────────────────

  getCalendarEvents: async (from: string, to: string): Promise<CalendarSowingEventDto[]> => {
    const { data } = await apiClient.get<ApiResponse<CalendarSowingEventDto[]>>(
      `${BASE}/calendar/events`,
      { params: { from, to } }
    );
    return data.data ?? [];
  },

  getPlannerInstructions: async (from: string, to: string): Promise<CalendarInstructionDto[]> => {
    const { data } = await apiClient.get<ApiResponse<CalendarInstructionDto[]>>(
      `${BASE}/calendar/instructions`,
      { params: { from, to } }
    );
    return data.data ?? [];
  },

  // ── Requisition Gating ────────────────────────────────────────────────────────

  getEligibleRequisitions: async (instructionId: string): Promise<EligibleRequisitionDto[]> => {
    const { data } = await apiClient.get<ApiResponse<EligibleRequisitionDto[]>>(
      `${BASE}/instructions/${instructionId}/eligible-requisitions`
    );
    return data.data ?? [];
  },

  // ── Stock Entry Pipeline (per-entry, multi-wave) ────────────────────────────

  getStockEntriesReadyForGrowing: async (): Promise<StockEntryPipelineDto[]> => {
    const { data } = await apiClient.get<ApiResponse<StockEntryPipelineDto[]>>(
      `${BASE}/stock-entries/ready-for-growing`
    );
    return data.data ?? [];
  },

  getStockEntriesGrowing: async (): Promise<StockEntryPipelineDto[]> => {
    const { data } = await apiClient.get<ApiResponse<StockEntryPipelineDto[]>>(
      `${BASE}/stock-entries/growing`
    );
    return data.data ?? [];
  },

  getStockEntriesReadyForDispatch: async (): Promise<StockEntryPipelineDto[]> => {
    const { data } = await apiClient.get<ApiResponse<StockEntryPipelineDto[]>>(
      `${BASE}/stock-entries/ready-for-dispatch`
    );
    return data.data ?? [];
  },

  getStockEntryPipelinePaged: async (params: {
    state: "growth" | "ready-for-growing" | "growing" | "ready-for-dispatch";
    pageNumber: number;
    pageSize: number;
    search?: string;
    sortBy?: string;
  }): Promise<PagedStockEntries> => {
    const q = new URLSearchParams();
    q.set("state", params.state);
    q.set("pageNumber", String(params.pageNumber));
    q.set("pageSize", String(params.pageSize));
    if (params.search?.trim()) q.set("search", params.search.trim());
    if (params.sortBy) q.set("sortBy", params.sortBy);
    const { data } = await apiClient.get<ApiResponse<PagedStockEntries>>(
      `${BASE}/stock-entries/paged?${q.toString()}`
    );
    return data.data;
  },

  acknowledgeStockEntry: async (
    entryId: string,
    payload: AcknowledgeStockEntryRequest
  ): Promise<StockEntryPipelineDto> => {
    const { data } = await apiClient.post<ApiResponse<StockEntryPipelineDto>>(
      `${BASE}/stock-entries/${entryId}/acknowledge`,
      payload
    );
    return data.data;
  },

  markEntryReadyForDispatch: async (
    entryId: string,
    payload: MarkEntryReadyForDispatchRequest
  ): Promise<StockEntryPipelineDto> => {
    const { data } = await apiClient.post<ApiResponse<StockEntryPipelineDto>>(
      `${BASE}/stock-entries/${entryId}/mark-ready`,
      payload
    );
    return data.data;
  },
};

// ─── Stock Entry Pipeline Types ─────────────────────────────────────────────

export interface StockEntryPipelineDto {
  id: string;
  isTrayStock?: boolean;
  sowingInstructionId: string;
  instructionNumber: string;
  sowingId?: string | null;
  germinationId?: string | null;
  cropTypeId: string;
  cropTypeName: string;
  variety?: string | null;
  trayType?: string | null;
  totalQuantity: number;
  fillingReserve: number;
  reservedQuantity: number;
  dispatchedQuantity: number;
  availableQuantity: number;
  physicalQuantity: number;
  greenhouseId?: string | null;
  greenhouseName?: string | null;
  greenhouseBedIds: string[];
  greenhouseBedAllocations?: { bedId: string; trayCount: number; wholeTrayCount: number }[];
  bedSummaries: string[];
  entryDate: string;
  sowingDate?: string | null;
  expectedReadyDate?: string | null;
  isActive: boolean;
  isReadyForDispatch: boolean;
  reservedForOrderNumber?: string | null;
  pipelineState: string;
  requiresBedAllocation: boolean;
}

export interface PagedStockEntries {
  items: StockEntryPipelineDto[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface AcknowledgeStockEntryRequest {
  greenhouseId: string;
  bedAllocations: BedTrayAllocation[];
  expectedReadyDate?: string | null;
  notes?: string;
}

export interface MarkEntryReadyForDispatchRequest {
  actualReadyDate: string;
  notes?: string;
}
